import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Package2, Layers, Trash2, Loader2, ImagePlus } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import {
  ProductGroupWithImages,
  ProductGroupImage,
  updateProductGroup,
  updateVariantFields,
  setVariantImage,
  setVariantStockQuantity,
  deleteProductGroup,
  linkProductGroupImages,
  deleteProductGroupImage,
} from '../lib/productGroups';
import MediaLibraryPicker from './MediaLibraryPicker';
import { MediaItem } from '../lib/mediaLibrary';
import { toast } from './toasts/SimpleToast';

interface ProductGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: ProductGroupWithImages | null;
  variants: StockItem[];
  onSaved?: () => void;
}

interface VariantDraft {
  id: string;
  label: string;
  unit: string;
  price: string;
  sku: string;
  quantity: string;
  originalPrice: number;
  originalSku: string | null;
  originalQuantity: number;
  imageId: string | null;
  originalImageId: string | null;
}

export default function ProductGroupModal({ isOpen, onClose, group, variants, onSaved }: ProductGroupModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [drafts, setDrafts] = useState<VariantDraft[]>([]);
  const [gallery, setGallery] = useState<ProductGroupImage[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!group) return;
    setTitle(group.title || '');
    setDescription(group.description || '');
    setVendor(group.vendor || '');
    setGallery(group.images || []);
    setConfirmDelete(false);
    setDrafts(
      variants.map(v => ({
        id: v.id,
        label: v.option_values ? Object.values(v.option_values).join(' / ') : v.name,
        unit: v.unit,
        price: String(v.price ?? 0),
        sku: v.sku || '',
        quantity: String(v.quantity ?? 0),
        originalPrice: v.price ?? 0,
        originalSku: v.sku ?? null,
        originalQuantity: v.quantity ?? 0,
        imageId: v.product_group_image_id ?? null,
        originalImageId: v.product_group_image_id ?? null,
      }))
    );
  }, [group, variants]);

  if (!isOpen || !group) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const updateDraft = (id: string, patch: Partial<VariantDraft>) => {
    setDrafts(prev => prev.map(d => (d.id === id ? { ...d, ...patch } : d)));
  };

  const handleAddMedia = async (selected: MediaItem[]) => {
    const existingUrls = new Set(gallery.map(g => g.url));
    const newRefs = selected
      .filter(m => !existingUrls.has(m.url))
      .map(m => ({ url: m.url, mediaId: m.id }));
    if (newRefs.length === 0) return;
    try {
      const created = await linkProductGroupImages(group.id, newRefs, gallery.length);
      setGallery(prev => [...prev, ...created]);
    } catch (err) {
      console.error('Failed to add media:', err);
      toast.error({ title: 'Failed to add media' });
    }
  };

  const handleRemoveGalleryImage = async (image: ProductGroupImage) => {
    try {
      await deleteProductGroupImage(image.id);
      setGallery(prev => prev.filter(g => g.id !== image.id));
      // Clear any variant draft that referenced the removed image.
      setDrafts(prev => prev.map(d => (d.imageId === image.id ? { ...d, imageId: null } : d)));
    } catch (err) {
      console.error('Failed to remove media:', err);
      toast.error({ title: 'Failed to remove media' });
    }
  };

  const handleSave = async () => {
    if (saving || deleting) return;
    setSaving(true);
    try {
      const groupChanged =
        title.trim() !== (group.title || '') ||
        description !== (group.description || '') ||
        vendor !== (group.vendor || '');
      if (groupChanged) {
        await updateProductGroup(group.id, {
          title: title.trim(),
          description: description.trim() || null,
          vendor: vendor.trim() || null,
        });
      }

      for (const d of drafts) {
        const newPrice = parseFloat(d.price) || 0;
        const newQty = parseInt(d.quantity, 10) || 0;
        const newSku = d.sku.trim() || null;

        if (newPrice !== d.originalPrice || newSku !== d.originalSku) {
          await updateVariantFields(d.id, { price: newPrice, sku: newSku });
        }
        if (d.imageId !== d.originalImageId) {
          const img = gallery.find(g => g.id === d.imageId) || null;
          await setVariantImage(d.id, img);
        }
        if (newQty !== d.originalQuantity) {
          await setVariantStockQuantity(d.id, newQty);
        }
      }

      toast.success({ title: 'Product updated' });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to save product:', err);
      toast.error({ title: 'Failed to save product', description: err instanceof Error ? err.message : undefined });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (saving || deleting) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await deleteProductGroup(group.id);
      toast.success({ title: 'Product deleted' });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete product:', err);
      toast.error({ title: 'Failed to delete product', description: err instanceof Error ? err.message : undefined });
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 bg-black/50"
      style={{ zIndex: 100060, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className={`bg-white ${
          isMobile ? 'relative w-full h-full' : 'fixed inset-y-0 right-0 w-full md:w-[640px] shadow-2xl'
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100070,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>
                Edit Product
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {drafts.length} {drafts.length === 1 ? 'variant' : 'variants'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6 space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Product fields */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Title</label>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Vendor</label>
                <input
                  value={vendor}
                  onChange={e => setVendor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Gallery */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Media</h3>
              <div className="flex flex-wrap gap-3">
                {gallery.map((img, i) => (
                  <div key={img.id} className="relative">
                    <img
                      src={img.url}
                      alt={`${title} ${i + 1}`}
                      className="h-20 w-20 object-cover rounded-xl border border-gray-200"
                      loading="lazy"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(img)}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-600 active:scale-95 transition-all"
                      aria-label="Remove image"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-blue-400 hover:bg-blue-50/40 active:scale-95 transition-all"
                  aria-label="Add media"
                >
                  <ImagePlus className="h-5 w-5 mb-0.5" />
                  <span className="text-[10px] font-medium">Add</span>
                </button>
              </div>
            </div>

            {/* Variants */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <Layers className="h-4 w-4 mr-2 text-gray-400" />
                Variants
              </h3>
              <div className="space-y-3">
                {drafts.map(d => (
                  <div key={d.id} className="p-3 rounded-xl border border-gray-200/70 space-y-2.5">
                    <div className="text-sm font-semibold text-gray-900">{d.label || '—'}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 mb-0.5">Price</label>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={d.price}
                          onChange={e => updateDraft(d.id, { price: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 mb-0.5">Stock</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={d.quantity}
                          onChange={e => updateDraft(d.id, { quantity: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 mb-0.5">SKU</label>
                        <input
                          value={d.sku}
                          onChange={e => updateDraft(d.id, { sku: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    {gallery.length > 0 && (
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 mb-1">Variant image</label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => updateDraft(d.id, { imageId: null })}
                            className={`h-12 w-12 rounded-lg border flex items-center justify-center transition-all ${
                              d.imageId === null ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                            }`}
                          >
                            <Package2 className="h-5 w-5 text-gray-300" />
                          </button>
                          {gallery.map(img => (
                            <button
                              type="button"
                              key={img.id}
                              onClick={() => updateDraft(d.id, { imageId: img.id })}
                              className={`h-12 w-12 rounded-lg overflow-hidden border transition-all ${
                                d.imageId === img.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                              }`}
                            >
                              <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Delete */}
            <button
              onClick={handleDelete}
              disabled={saving || deleting}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                confirmDelete
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
              } disabled:opacity-50`}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {confirmDelete ? 'Tap again to confirm delete' : 'Delete product'}
            </button>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving || deleting}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || deleting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <MediaLibraryPicker
        isOpen={pickerOpen}
        businessId={group.business_id}
        multiple
        initialSelectedUrls={gallery.map(g => g.url)}
        onClose={() => setPickerOpen(false)}
        onDone={handleAddMedia}
      />
    </div>
  );

  return createPortal(content, document.body);
}
